import React from "react";

import { useDndContext } from "@dnd-kit/core";
import { useSortable } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { Box } from "@mui/material";

import { ENTITY_DRAG } from "@/components/alerts/add-v2/model/dropIntent";

/**
 * One slot in the entity list, positioned by dnd-kit.
 *
 * The wrapper exists because a row is two different components — the collapsed `EntityCard` and the
 * open `EntityEditor` — and **both** have to hold a sortable slot, or dragging across the open one
 * would jump over a gap dnd-kit does not know the height of. Only the card is handed a handle: the
 * editor is a form being typed into, and moving it while it is open is not a gesture anyone wants.
 *
 * `CSS.Translate`, not `CSS.Transform`: the open editor is many times the height of a card, and the
 * scale half of a transform stretches the row being dragged past it.
 */

/** What a row needs to turn one of its own buttons into the drag handle. */
export type EntityDragHandle = Pick<
  ReturnType<typeof useSortable>,
  "attributes" | "listeners" | "setActivatorNodeRef"
>;

interface SortableEntityProps {
  id: string;
  /**
   * `mediaOver` is true while a **file** is being held over this row. A sortable is already a drop
   * target, so the card needs no drop handler of its own — what it needs is to know that the thing
   * over it is a file rather than another entity, because only one of those means "attach".
   */
  children: (handle: EntityDragHandle, mediaOver: boolean) => React.ReactNode;
}

export const SortableEntity: React.FC<SortableEntityProps> = ({
  id,
  children,
}) => {
  const {
    attributes,
    listeners,
    setActivatorNodeRef,
    setNodeRef,
    transform,
    transition,
    isDragging,
    isOver,
  } = useSortable({ id, data: ENTITY_DRAG });

  const { active } = useDndContext();
  const mediaOver = isOver && active?.data.current?.kind === "media";

  return (
    <Box
      ref={setNodeRef}
      // The row being dragged has to sit above the ones it passes, and above the section's own
      // stacking — a card is opaque, so without this it slides underneath its neighbours.
      sx={{ position: "relative", zIndex: isDragging ? 1 : 0 }}
      style={{
        transform: CSS.Translate.toString(transform),
        transition,
      }}
    >
      {children({ attributes, listeners, setActivatorNodeRef }, mediaOver)}
    </Box>
  );
};
